import React, { ReactNode, useEffect } from 'react';
import { colors, typography, spacing } from '../../styles/design-system';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children?: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
}

// Confirm Modal Props
interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string | ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'primary' | 'danger';
  loading?: boolean;
}

const getModalWidth = (size: ModalSize): string => {
  switch (size) {
    case 'sm':
      return '400px';
    case 'md':
      return '560px';
    case 'lg':
      return '720px';
    case 'xl':
      return '960px';
    case 'full':
      return '95vw';
    default:
      return '560px';
  }
};

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  closeOnOverlayClick = true,
  closeOnEscape = true,
  showCloseButton = true,
}) => {
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <div
        style={{
          ...styles.modal,
          maxWidth: getModalWidth(size),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || showCloseButton) && (
          <div style={styles.header}>
            {title && <h3 style={styles.title}>{title}</h3>}
            {showCloseButton && (
              <button style={styles.closeButton} onClick={onClose}>
                &times;
              </button>
            )}
          </div>
        )}
        {children && <div style={styles.body}>{children}</div>}
        {footer && <div style={styles.footer}>{footer}</div>}
      </div>
    </div>
  );
};

// Confirm Modal Component
export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = '확인',
  cancelText = '취소',
  confirmVariant = 'primary',
  loading = false,
}) => {
  const confirmButtonStyle = {
    ...styles.button,
    ...(confirmVariant === 'danger' ? styles.buttonDanger : styles.buttonPrimary),
    opacity: loading ? 0.7 : 1,
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
      <div style={styles.confirmBody}>
        {typeof message === 'string' ? <p>{message}</p> : message}
      </div>
      <div style={styles.confirmFooter}>
        <button
          style={{ ...styles.button, ...styles.buttonSecondary }}
          onClick={onClose}
          disabled={loading}
        >
          {cancelText}
        </button>
        <button
          style={confirmButtonStyle}
          onClick={onConfirm}
          disabled={loading}
        >
          {loading ? '처리 중...' : confirmText}
        </button>
      </div>
    </Modal>
  );
};

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: spacing[5],
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '4px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${spacing[5]} ${spacing[7]}`,
    borderBottom: `1px solid ${colors.gray[200]}`,
  },
  title: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    color: colors.black,
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    color: colors.gray[500],
    cursor: 'pointer',
    padding: 0,
    lineHeight: 1,
    transition: 'color 0.15s',
  },
  body: {
    padding: spacing[7],
    overflowY: 'auto',
    flex: 1,
  },
  footer: {
    padding: `${spacing[5]} ${spacing[7]}`,
    borderTop: `1px solid ${colors.gray[200]}`,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: spacing[3],
  },
  confirmBody: {
    textAlign: 'center',
    padding: `${spacing[5]} 0`,
    color: colors.gray[700],
  },
  confirmFooter: {
    display: 'flex',
    justifyContent: 'center',
    gap: spacing[3],
    paddingTop: spacing[5],
    borderTop: `1px solid ${colors.gray[200]}`,
    marginTop: spacing[5],
  },
  button: {
    padding: `${spacing[2]} ${spacing[6]}`,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    border: '1px solid',
    borderRadius: '0',
    cursor: 'pointer',
    transition: 'all 0.15s',
    minWidth: '80px',
  },
  buttonPrimary: {
    backgroundColor: colors.black,
    color: colors.white,
    borderColor: colors.black,
  },
  buttonSecondary: {
    backgroundColor: colors.white,
    color: colors.black,
    borderColor: colors.gray[300],
  },
  buttonDanger: {
    backgroundColor: colors.status.error,
    color: colors.white,
    borderColor: colors.status.error,
  },
};

export default Modal;
